<template>
  <Container padding-top full-width :class="$style['page']">
    <Breadcrumb :items="[{ label: 'Kleding verhuur', to: { name: 'kleding' } }, { label: category?.title ?? '' }]" />

    <h1 :class="$style['title']">{{ category?.title }}</h1>
    <p v-if="category?.description" :class="$style['lead']">{{ category.description }}</p>

    <!-- Where the list starts: after a filter change the page comes back here -->
    <div ref="resultsStart" :class="$style['anchor']" />

    <!-- One bar above the items: switch category, filter, and the count, all on one line.
         On larger screens it stays in view under the header. -->
    <div ref="toolbar" :class="$style['bar']">
      <div :class="$style['bar__menus']">
        <MenuButton label="Categorieën">
          <ul :class="$style['links']">
            <li v-for="item in sortedCategories" :key="item.id">
              <NuxtLink
                :to="{ name: 'kleding-slug', params: { slug: item.slug } }"
                :aria-current="item.slug === category?.slug ? 'page' : undefined"
                :class="$style['links__item']">
                {{ item.title }}
                <span :class="$style['links__count']">{{ item.products_count }}</span>
              </NuxtLink>
            </li>
          </ul>
        </MenuButton>
        <MenuButton v-if="colors?.length" label="Kleuren" :count="filters.colors.length" :class="$style['bar__desktop']">
          <div :class="[$style['options'], $style['options--colors']]">
            <FormCheckbox
              v-for="item in colors"
              :key="item.slug"
              :label="item.title.trim()"
              :checked="filters.colors.includes(item.slug)"
              @change="handleToggleFilter({ type: 'colors', value: item.slug })">
              <span
                :class="[$style['swatch'], item.slug === 'multicolor' && $style['swatch--multi']]"
                :style="item.slug === 'multicolor' ? undefined : { backgroundColor: item.hex }"
                aria-hidden="true" />
            </FormCheckbox>
          </div>
        </MenuButton>
        <MenuButton v-if="themes?.length" label="Thema's" :count="filters.themes.length" :class="$style['bar__desktop']">
          <div :class="[$style['options'], $style['options--themes']]">
            <FormCheckbox
              v-for="item in themes"
              :key="item.slug"
              :label="item.title.trim()"
              :checked="filters.themes.includes(item.slug)"
              @change="handleToggleFilter({ type: 'themes', value: item.slug })" />
          </div>
        </MenuButton>
        <!-- Phones: colours and themes together in a sheet -->
        <button
          ref="filtersToggle"
          type="button"
          aria-haspopup="dialog"
          :class="[$style['bar__sheet'], $style['bar__mobile']]"
          @click="openFilters">
          <Icon name="ListFilter" />
          Filters
          <span v-if="activeFilters.length" :class="$style['bar__badge']">{{ activeFilters.length }}</span>
        </button>
      </div>

      <ul v-if="activeFilters.length" :class="$style['active']" aria-label="Gekozen filters">
        <li v-for="filter in activeFilters" :key="`${filter.type}-${filter.value}`">
          <button type="button" :class="$style['active__chip']" @click="handleToggleFilter(filter)">
            {{ filter.title }} <Icon name="X" /><span :class="$style['sr-only']"> weghalen</span>
          </button>
        </li>
        <li>
          <button type="button" :class="$style['active__clear']" @click="clearFilters">Wis alle filters</button>
        </li>
      </ul>

      <!-- On a phone the chips under it already show the filters, so the count stays short -->
      <p :class="$style['count']" role="status">
        {{ itemsLabel(total) }}<span v-if="activeFilters.length" :class="$style['count__extra']">{{ countLabel.slice(itemsLabel(total).length) }}</span>
      </p>
    </div>

    <!-- Phones: the filters open in a sheet over the page -->
    <dialog ref="filtersDialog" :class="$style['sheet']" aria-labelledby="filters-title" @click="onDialogClick" @close="onFiltersClose" @cancel="sheetExit.onCancel">
      <div :class="$style['sheet__head']">
        <h2 id="filters-title" :class="$style['sheet__title']">Filters</h2>
        <Button variant="ghost" icon="X" type="button" @click="closeFilters">Sluiten</Button>
      </div>
      <div :class="$style['sheet__body']">
        <ProductFilters
          :colors="colors || []"
          :themes="themes || []"
          :filters="filters"
          hide-categories
          @toggle-filter="handleToggleFilter" />
      </div>
      <div :class="$style['sheet__foot']">
        <Button v-if="activeFilters.length" variant="ghost" type="button" @click="clearFilters">Wis filters</Button>
        <Button variant="primary" type="button" :class="$style['sheet__show']" @click="closeFilters">{{ showLabel }}</Button>
      </div>
    </dialog>

    <!-- Dims while new results load, so a click on a filter always shows something happening -->
    <ul
      v-if="products.length > 0"
      :aria-busy="updating || undefined"
      :class="[$style['grid'], updating && $style['grid--updating']]">
      <li v-for="product in products" :key="product.id">
        <ProductCard
          :product="product"
          :to="{ name: 'kleding-product-slug-id', params: { id: product.id, slug: product.slug } }" />
      </li>
    </ul>

    <div v-else :class="$style['empty']">
      <template v-if="activeFilters.length">
        <p>Geen {{ category?.title.toLowerCase() }} met deze filters.</p>
        <Button variant="primary" type="button" @click="clearFilters">Wis alle filters</Button>
      </template>
      <template v-else>
        <p>Er staan nog geen items in deze categorie.</p>
        <Button variant="outline" :to="{ name: 'kleding' }">Bekijk alle categorieën</Button>
      </template>
    </div>

    <!-- Loads more by itself when it comes into view; the button is for keyboards and slow scrolling -->
    <div v-if="hasMore" ref="loadMoreRef" :class="$style['load-more']">
      <Button variant="outline" :loading="loading" @click="loadMore">
        Toon meer
      </Button>
    </div>
    <div ref="listEnd" />

    <!-- Phones: deep in a long list the filters stay one tap away -->
    <div :class="[$style['float'], floatVisible && $style['float--visible']]" :inert="!floatVisible || undefined">
      <Button
        icon="ListFilter"
        type="button"
        aria-haspopup="dialog"
        :class="$style['float__button']"
        @click="openFilters">
        Filters{{ activeFilters.length ? ` (${activeFilters.length})` : '' }}
      </Button>
    </div>
  </Container>
</template>

<script setup lang="ts">
import { useCssModule } from 'vue'

interface Category {
  id: number
  slug: string
  title: string
  description?: string
}

interface FilterItem {
  id: number
  slug: string
  title: string
  hex?: string
}

interface Product {
  id: number
  slug: string
  title: string
  images?: string[]
  colors?: FilterItem[]
}

interface CategoryItem {
  id: number
  slug: string
  title: string
  products_count: number
}

const route = useRoute()
const router = useRouter()
const { formatCategory } = useHrMeta()
definePageMeta({
  layout: 'rental'
})

// Fetch category details by slug
const { data: category, error: categoryError } = await useApi<Category>(`/categories/${route.params.slug}`)

// Handle 404
if (categoryError.value || !category.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Categorie niet gevonden'
  })
}

// Every category, the largest first, to switch without going back (the same list as the overview)
const { data: allCategories } = await useApi<CategoryItem[]>('/categories', { params: { nonEmpty: 1 } })
const sortedCategories = computed(() => [...(allCategories.value ?? [])].sort((a, b) => b.products_count - a.products_count))

// Fetch filter options — scoped to the current category
// Explicit keys include the slug so Nuxt does not share the cached result across different category pages
const { data: themes } = await useApi<FilterItem[]>('/themes', { key: `themes-${route.params.slug}`, params: { categories: route.params.slug } })
const { data: colors } = await useApi<FilterItem[]>('/colors', { key: `colors-${route.params.slug}`, params: { categories: route.params.slug } })

// Initialize filters from URL query params
const parseQueryArray = (param: string | string[] | undefined): string[] => {
  if (!param) { return [] }
  if (Array.isArray(param)) { return param }
  return param.split(',').filter(Boolean)
}

const filters = ref<{
  categories: string[]
  colors: string[]
  themes: string[]
}>({
  categories: [],
  colors: parseQueryArray(route.query.colors as string),
  themes: parseQueryArray(route.query.themes as string)
})

const limit = ref(20)
const filtersOpen = ref(false)
const filtersDialog = ref<HTMLDialogElement>()
const loading = ref(false)

const params = computed(() => {
  return {
    categories: [category.value!.slug],
    colors: filters.value.colors,
    themes: filters.value.themes,
    limit: limit.value
  }
})

const { data: productsResponse, refresh, status } = await useApi<{data: Product[], pagination: {hasMore: boolean, nextCursor: number | null, limit: number, total?: number}}>('/products', {
  params,
  watch: [params]
})

const products = computed(() => productsResponse.value?.data || [])
// New results on the way because a filter changed (loading more has its own spinner)
const updating = computed(() => status.value === 'pending' && !loading.value)
const hasMore = computed(() => productsResponse.value?.pagination?.hasMore || false)
const total = computed(() => productsResponse.value?.pagination?.total ?? products.value.length)

// The chosen filters with their names, for the chips above the items
const activeFilters = computed(() => [
  ...filters.value.colors.map(value => ({ type: 'colors' as const, value, title: colors.value?.find(item => item.slug === value)?.title.trim() ?? value })),
  ...filters.value.themes.map(value => ({ type: 'themes' as const, value, title: themes.value?.find(item => item.slug === value)?.title.trim() ?? value }))
])

const itemsLabel = (count: number) => `${count} ${count === 1 ? 'item' : 'items'}`
const countLabel = computed(() => activeFilters.value.length
  ? `${itemsLabel(total.value)} met ${activeFilters.value.length === 1 ? 'dit filter' : 'deze filters'}`
  : itemsLabel(total.value))
const showLabel = computed(() => total.value ? `Toon ${itemsLabel(total.value)}` : 'Geen items met deze filters')

// Update URL when filters change
const updateUrl = () => {
  const query: Record<string, string> = {}

  if (filters.value.colors.length > 0) {
    query.colors = filters.value.colors.join(',')
  }
  if (filters.value.themes.length > 0) {
    query.themes = filters.value.themes.join(',')
  }

  router.replace({ query })
}

// After a filter change the list starts over: bring its start into view if you had scrolled past it.
// With the sheet open that waits until it closes.
const toolbar = ref<HTMLElement>()
const resultsStart = ref<HTMLElement>()
const filtersToggle = ref<HTMLButtonElement>()
let changedInSheet = false
const showResultsStart = () => {
  const element = resultsStart.value
  if (!element) { return }
  if (filtersOpen.value) {
    changedInSheet = true
    return
  }
  const offset = parseFloat(getComputedStyle(element).scrollMarginTop) || 0
  if (element.getBoundingClientRect().top >= offset) { return }
  const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  element.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'instant' })
}

const handleToggleFilter = ({ type, value }: {type: 'categories' | 'colors' | 'themes', value: string}) => {
  if (type === 'categories') { return } // Category is locked

  if (filters.value[type].includes(value)) {
    filters.value[type] = filters.value[type].filter((item: string) => item !== value)
  } else {
    filters.value[type] = [...filters.value[type], value]
  }
  limit.value = 20

  updateUrl()
  showResultsStart()
}

const clearFilters = () => {
  filters.value = { categories: [], colors: [], themes: [] }
  limit.value = 20
  updateUrl()
  showResultsStart()
}

const openFilters = () => {
  filtersDialog.value?.showModal()
  filtersOpen.value = true
}

// The sheet slides back down when it closes
const sheetExit = useDialogExit(filtersDialog, useCssModule()['sheet--closing']!)
const closeFilters = () => sheetExit.close()

// The floating button you came from is gone once the list starts over: the keyboard goes to the one above the list
const onFiltersClose = () => {
  filtersOpen.value = false
  if (!changedInSheet) { return }
  changedInSheet = false
  filtersToggle.value?.focus({ preventScroll: true })
  showResultsStart()
}

// A click on the dimmed area around the sheet closes it
const onDialogClick = (event: MouseEvent) => {
  if (event.target === filtersDialog.value) { closeFilters() }
}

const loadMore = async () => {
  loading.value = true
  limit.value += 20
  await refresh()
  loading.value = false
}

// Loads more on its own when the end of the list comes near. The element comes and goes with the
// filters, so each new one is watched again.
const loadMoreRef = ref<HTMLElement | null>(null)
let moreObserver: IntersectionObserver | undefined

// The floating filter button: once the toolbar has scrolled away, until the end of the list
const listEnd = ref<HTMLElement>()
const toolbarAbove = ref(false)
const endInView = ref(false)
const floatVisible = computed(() => toolbarAbove.value && !endInView.value && !filtersOpen.value)
let floatObserver: IntersectionObserver | undefined

onMounted(() => {
  moreObserver = new IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting) && hasMore.value && !loading.value) { loadMore() }
  }, { rootMargin: '100px' }) // Start loading 100px before the element is visible
  if (loadMoreRef.value) { moreObserver.observe(loadMoreRef.value) }

  floatObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.target === toolbar.value) {
        toolbarAbove.value = !entry.isIntersecting && entry.boundingClientRect.top < 0
      } else {
        endInView.value = entry.isIntersecting
      }
    }
  })
  if (toolbar.value) { floatObserver.observe(toolbar.value) }
  if (listEnd.value) { floatObserver.observe(listEnd.value) }
})

watch(loadMoreRef, (element, before) => {
  if (before) { moreObserver?.unobserve(before) }
  if (element) { moreObserver?.observe(element) }
})

onUnmounted(() => {
  moreObserver?.disconnect()
  floatObserver?.disconnect()
})

formatCategory({ category, products, total })
</script>

<style module>
.page {
  display: flex;
  flex-direction: column;
}

.title {
  margin: var(--spacing-xl) 0 0;
  font-size: var(--stage-title-page);
  line-height: 1;
}

.lead {
  max-width: var(--stage-measure);
  margin: var(--spacing-l) 0 0;
  color: var(--stage-copy);
  font-size: var(--stage-text);
}

/* Sits right above the bar, so the page can scroll back to the start of the list */
.anchor {
  scroll-margin-top: var(--stage-header);
}

.bar {
  position: sticky;
  top: var(--stage-header);
  z-index: 20;
  display: grid;
  grid-template: "menus active count" auto / auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--spacing-m) var(--spacing-l);
  margin: var(--spacing-xl) 0 var(--spacing-xxl);
  padding-block: var(--spacing-m);
  border-bottom: var(--stage-hairline) solid var(--stage-line-soft);
  background: var(--stage-black);
}

.bar__menus {
  grid-area: menus;
  display: flex;
  gap: var(--spacing);
}

.bar__mobile {
  display: none;
}

/* The same pill as the menu buttons next to it */
.bar__sheet {
  align-items: center;
  gap: var(--spacing);
  min-height: 2.5rem;
  padding: 0 var(--spacing-l);
  border: 0;
  border-radius: 999rem;
  background: var(--opacity-surface);
  color: var(--text-base);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
}

.bar__sheet svg {
  width: 1rem;
  height: 1rem;
}

.bar__badge {
  display: inline-grid;
  place-items: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding-inline: var(--spacing-s);
  border-radius: 999rem;
  background: var(--primary-color);
  color: var(--stage-black);
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
}

.count {
  grid-area: count;
  margin: 0;
  color: var(--text-subtle);
  font-size: var(--font-size-small);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* The category menu: every category with its count, the current one marked */
.links {
  display: grid;
  grid-template-columns: repeat(3, minmax(9rem, 1fr));
  gap: 0 var(--spacing-l);
  margin: 0;
  padding: 0;
  list-style: none;
}

.links__item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-m);
  padding: var(--spacing) var(--spacing-m);
  color: var(--text-base);
  font-size: var(--font-size-small);
  text-decoration: none;
}

.links__item:hover {
  background: var(--opacity-surface);
}

.links__item[aria-current] {
  color: var(--primary-color);
  font-weight: var(--font-weight-bold);
}

.links__count {
  color: var(--text-subtle);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.options {
  display: grid;
  gap: var(--spacing-m) var(--spacing-xl);
  font-size: var(--font-size-small);
}

.options--colors {
  grid-template-columns: repeat(2, minmax(8rem, 1fr));
}

.options--themes {
  grid-template-columns: repeat(3, minmax(8rem, 1fr));
}

.swatch {
  flex: none;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  box-shadow: inset 0 0 0 var(--stage-hairline) var(--stage-line);
}

.swatch--multi {
  background:
    conic-gradient(
      oklch(65% 0.25 29deg),
      oklch(85% 0.17 90deg),
      oklch(70% 0.2 145deg),
      oklch(60% 0.2 260deg),
      oklch(60% 0.25 330deg),
      oklch(65% 0.25 29deg)
    );
}

.active {
  grid-area: active;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing);
  margin: 0;
  padding: 0;
  list-style: none;
}

.active__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-s);
  min-height: 2rem;
  padding: 0 var(--spacing-m) 0 var(--spacing-l);
  border: var(--stage-hairline) solid var(--primary-color);
  border-radius: 999rem;
  background: transparent;
  color: var(--text-base);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
}

.active__chip:hover {
  background: var(--primary-color);
  color: var(--stage-black);
}

.active__chip svg {
  width: 0.875rem;
  height: 0.875rem;
}

.active__clear {
  min-height: 2rem;
  padding: 0 var(--spacing-m);
  border: 0;
  background: none;
  color: var(--text-subtle);
  font: inherit;
  font-size: var(--font-size-small);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
}

.active__clear:hover {
  color: var(--text-base);
}

.sr-only {
  position: absolute;
  width: var(--stage-hairline);
  height: var(--stage-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Five across on a laptop: the photos carry the page */
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr));
  gap: var(--spacing-xxl) var(--spacing-xl);
  margin: 0;
  padding: 0;
  list-style: none;
  transition: opacity 200ms;
}

/* Only when it takes a moment, so a quick answer does not flicker */
.grid--updating {
  opacity: 0.4;
  transition-delay: 150ms;
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-l);
  padding: var(--spacing-xxxl) 0;
}

.empty p {
  margin: 0;
  font-size: var(--stage-text);
}

.load-more {
  display: flex;
  justify-content: center;
  padding: var(--spacing-xxl) 0;
}

/* The filter sheet for phones */
.sheet {
  width: 100%;
  max-width: 100%;
  max-height: min(85vh, 40rem);
  margin: auto 0 0;
  padding: 0;
  border: 0;
  border-top: var(--stage-hairline) solid var(--stage-line);
  background: var(--background-raised);
  color: var(--text-base);
}

.sheet[open] {
  display: flex;
  flex-direction: column;
  animation: sheet-in 300ms var(--stage-ease);
}

.sheet[open]::backdrop {
  background: oklch(0% 0 0deg / 70%);
  animation: sheet-backdrop-in 300ms ease-out;
}

/* Slides up from the bottom, where the thumb is */
@keyframes sheet-in {
  from {
    transform: translateY(100%);
  }
}

@keyframes sheet-backdrop-in {
  from {
    opacity: 0;
  }
}

/* The way out: back down */
.sheet--closing[open] {
  animation: sheet-out 300ms var(--stage-ease) forwards;
}

.sheet--closing[open]::backdrop {
  animation: sheet-backdrop-out 300ms ease-in forwards;
}

@keyframes sheet-out {
  to {
    transform: translateY(100%);
  }
}

@keyframes sheet-backdrop-out {
  to {
    opacity: 0;
  }
}

.sheet__head,
.sheet__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-m);
  padding: var(--spacing-m) var(--spacing-xl);
}

.sheet__head {
  border-bottom: var(--stage-hairline) solid var(--stage-line);
}

/* The page has one big title; this one is bold but in sentence case */
.sheet__title {
  margin: 0;
  font-family: var(--font-family);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-large);
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: none;
}

.sheet__body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-xl);
  overscroll-behavior: contain;
}

.sheet__foot {
  border-top: var(--stage-hairline) solid var(--stage-line);
}

.sheet__show {
  flex: 1;
}

.float {
  position: fixed;
  bottom: calc(var(--spacing-xl) + env(safe-area-inset-bottom, 0rem));
  left: 50%;
  z-index: 900;
  display: none;
  opacity: 0;
  transform: translate(-50%, 150%);
  transition: opacity 250ms, transform 300ms var(--stage-ease);
}

.float--visible {
  opacity: 1;
  transform: translate(-50%, 0);
}

/* Solid black, so it reads over the photos */
.float .float__button {
  --background-color: var(--stage-black);

  box-shadow: inset 0 0 0 var(--stage-hairline) var(--stage-line), 0 0.5rem 2rem oklch(0% 0 0deg / 70%);
}

/* Phones: the bar scrolls with the page (the floating button takes over), colours and themes in the sheet */
@media (width <= 50rem) {
  .bar {
    position: static;
    grid-template:
      "menus count" auto
      "active active" auto
      / auto minmax(0, 1fr);
    margin-bottom: var(--spacing-xl);
  }

  .bar__desktop {
    display: none;
  }

  .bar__mobile {
    display: inline-flex;
  }

  .count {
    justify-self: end;
  }

  .count__extra {
    display: none;
  }

  .links {
    grid-template-columns: repeat(2, minmax(8rem, 1fr));
  }

  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--spacing-xl) var(--spacing-l);
  }

  .float {
    display: block;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sheet[open],
  .sheet[open]::backdrop {
    animation: none;
  }

  .float {
    transition: opacity 250ms;
  }
}
</style>
